import React, { useState, useEffect } from 'react';
import { Button, Row, Col, FormGroup, Label, CloseButton, Spinner } from 'reactstrap';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { useModuleTopics, MODULE_PARENT_TOPIC_ID } from 'app/shared/common-fun/useModuleTopics';
import { createEntity, updateEntity } from 'app/entities/housing/housing.reducer';
import axios from 'axios';
import { checkValid, renderPreview, validateWordLimit } from 'app/shared/common-fun';
import Select, { components } from 'react-select';
import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';

const Option = props => (
  <components.Option {...props}>
    <input type="checkbox" checked={props.isSelected} onChange={() => null} style={{ marginRight: 8 }} />
    <label>{props.label}</label>
  </components.Option>
);

export const HousingUpdate = props => {
  const dispatch = useAppDispatch();
  const housingEntity = props.housing;
  const isNew = !housingEntity;
  const { options: topicOptions } = useModuleTopics(MODULE_PARENT_TOPIC_ID.housing);
  const updating = useAppSelector(state => state.housing.updating);
  const account = useAppSelector(state => state.authentication.account);
  const userid = account.id;

  const [error, setError] = useState('');
  const [fileError, setFileError] = useState('');
  const [apiError, setApiError] = useState('');
  const [description, setDescription] = useState(housingEntity?.description ?? '');
  const [imageName, setImageName] = useState(housingEntity?.imageName ?? null);
  const [uploading, setUploading] = useState(false);
  const [housingTopics, setHousingTopics] = useState([]);

  // only the sub topics of this module are offered, so a saved topic that is no longer one of them is dropped
  useEffect(() => {
    if (housingEntity?.topics) {
      const selectedTopicNames = housingEntity.topics.split(',').map(topic => topic.trim());
      setHousingTopics(topicOptions.filter(option => selectedTopicNames.includes(option.label)));
    }
  }, [topicOptions]);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const errorMsg = checkValid(event);
    setFileError(errorMsg ?? '');
    if (errorMsg) return;
    setApiError('');
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const formData = new FormData();
      formData.append('image', selectedFile);
      setUploading(true);
      axios
        .post('/api/upload', formData)
        .then(response => {
          setImageName(response.data);
          setFileError('');
        })
        .catch(() => setFileError('Error uploading image. Please try again.'))
        .finally(() => setUploading(false));
    }
  };

  const handleDescriptionChange = value => {
    const limitError = validateWordLimit(value, 50, 'housing');
    if (!limitError) setDescription(value);
    setError(limitError ?? '');
  };

  const saveEntity = async values => {
    if (!imageName && !values.link?.trim()) {
      // a rejected file keeps its own message instead of the generic one
      setApiError(fileError ? '' : 'Please upload a file or enter a link. At least one is required');
      return;
    }
    setApiError('');

    values.createdDate = convertDateTimeToServer(values.createdDate);
    if (!isNew) {
      values.modifiedDate = new Date();
    }

    const entity = {
      ...housingEntity,
      ...values,
      description,
      imageName: imageName || null,
      topics: housingTopics.map(opt => opt.label).join(', '),
      user: { id: userid },
      approved: true, // no admin approval needed for this module
    };

    const resultAction = await dispatch(isNew ? createEntity(entity) : updateEntity(entity));
    if (createEntity.rejected.match(resultAction) || updateEntity.rejected.match(resultAction)) {
      setApiError('An error occurred while saving. Please try again.');
      return;
    }
    props.handleSubmit(isNew ? 'created' : 'updated');
  };

  const defaultValues = () =>
    isNew
      ? {
          createdDate: new Date(),
        }
      : {
          ...housingEntity,
          createdDate: convertDateTimeFromServer(housingEntity.createdDate),
          modifiedDate: convertDateTimeFromServer(new Date()),
          description,
          user: housingEntity?.user?.id,
        };

  return (
    <div className="card meeting-border edit-create-pages">
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="fivePlusApp.housing.home.createOrEditLabel" data-cy="HousingCreateUpdateHeading">
            <label>Create or edit a Housing</label>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
            <FormGroup>
              <Label for="housing-description">
                {translate('fivePlusApp.housing.description')}
                <span className="text-danger"> *</span>
              </Label>
              <ValidatedField
                id="housing-description"
                name="description"
                data-cy="description"
                type="textarea"
                value={description}
                onChange={e => handleDescriptionChange(e.target.value)}
                placeholder="Enter housing details (max 50 words)"
                required
              />
              {error && <div className="text-danger">{error}</div>}
            </FormGroup>
            <ValidatedField
              id="images-file"
              name="imageName"
              data-cy="imageName"
              type="file"
              label={'Choose File'}
              className="col-12 col-md-6 col-lg-4 position-raelative"
              onChange={handleFileInputChange}
              valid={false}
            />
            {fileError && <div className="text-danger small mb-2">{fileError}</div>}
            <Col md={12} className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
                <span>
                  <FontAwesomeIcon icon="info-circle" className="me-1" />
                  Supported files:
                </span>
                <span className="badge rounded-pill" style={{ backgroundColor: '#e7f1ff', color: '#0d6efd', border: '1px solid #0d6efd' }}>
                  Images: JPG, JPEG, PNG, GIF, WEBP
                </span>
                <span className="badge rounded-pill" style={{ backgroundColor: '#fdeaea', color: '#dc3545', border: '1px solid #dc3545' }}>
                  PDF
                </span>
                <span className="badge rounded-pill" style={{ backgroundColor: '#e6f6ec', color: '#198754', border: '1px solid #198754' }}>
                  Video/Audio: MP4, MOV, WEBM, OGG, AVI, MKV, MP3
                </span>
                <span className="badge rounded-pill" style={{ backgroundColor: '#eef0f2', color: '#6c757d', border: '1px solid #6c757d' }}>
                  Max 50MB
                </span>
              </div>
            </Col>
            {uploading && <Spinner className="body-loader-icon" />}
            {imageName ? (
              <Col md={12}>
                <div className="remove-img-btn">
                  <CloseButton
                    onClick={() => {
                      setImageName(null);
                      setFileError('');
                    }}
                  />
                </div>
                {renderPreview(imageName)}
              </Col>
            ) : null}
            <div className="align-items-center d-flex form-title pb-1">
              <h3 className="pt-3">Topics</h3> <hr className="w-100 m-0 ms-3" />
            </div>
            <div id="topic-ques" className="mb-3">
              <Select
                options={topicOptions}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option }}
                onChange={selected => setHousingTopics(selected ? [...selected] : [])}
                value={housingTopics}
                placeholder="Select topics..."
              />
            </div>
            <ValidatedField
              label={translate('fivePlusApp.housing.link')}
              id="housing-link"
              name="link"
              data-cy="link"
              type="text"
              onChange={() => apiError && setApiError('')}
              validate={{
                pattern: {
                  value: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
                  message: 'Please enter a valid URL',
                },
              }}
              valid={false}
            />
            {apiError && <div className="text-danger small mb-2">{apiError}</div>}
            &nbsp;
            <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
              <FontAwesomeIcon icon="save" />
              &nbsp;
              <Translate contentKey="entity.action.save">Submit</Translate>
            </Button>
          </ValidatedForm>
        </Col>
      </Row>
    </div>
  );
};

export default HousingUpdate;
